/**
 * Terms – Algemene voorwaarden
 * Afgestemd op docs/FASEPLAN_CALQTOOL.md (Fase 2: proefperiode, pakketten).
 */
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

const TERMS_LAST_UPDATED = '31 maart 2026';

export function Terms() {
  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/terms"
        title="Algemene voorwaarden"
        description="Algemene voorwaarden CalqTool™: gebruik van het platform, licenties, aansprakelijkheid, intellectueel eigendom en toepasselijk recht — lees vóór gebruik."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Voorwaarden' }]} />
        <h1 className="mb-6 text-2xl font-bold text-text">Algemene voorwaarden</h1>
        <div className="max-w-3xl space-y-6 rounded-lg border border-border bg-surface-card p-6">
          <p className="text-sm text-text-muted">Laatst bijgewerkt: {TERMS_LAST_UPDATED}</p>
          <p className="rounded-md border border-border/80 bg-surface px-3 py-2 text-xs text-text-muted">
            Waar deze voorwaarden strijdig zijn met <strong className="text-text">dwingend recht</strong> (waaronder
            bepalingen voor consumenten), prevaleert dat dwingende recht.
          </p>
          <section>
            <h2 className="mb-2 font-semibold text-text">1. Toepasselijkheid</h2>
            <div className="space-y-3 text-sm text-text-muted">
              <p>
                Deze voorwaarden zijn van toepassing op het gebruik van CalqTool™ en alle daaraan verbonden diensten
                (webapplicatie, API voor zover aan u beschikbaar gesteld, en ondersteuning). De identiteit van de
                aanbieder vindt u op de{' '}
                <Link to="/contact" className="font-medium text-primary hover:underline">
                  contactpagina
                </Link>{' '}
                en in het{' '}
                <Link to="/privacy" className="font-medium text-primary hover:underline">
                  privacybeleid
                </Link>
                .
              </p>
              <p>
                <strong className="text-text">Doelgroep.</strong> CalqTool™ richt zich primair op{' '}
                <strong className="text-text">zakelijke gebruikers</strong> (bedrijven, instellingen, zelfstandigen in
                professionele hoedanigheid). Een deel van de gebruikers kan een <strong className="text-text">consument</strong>{' '}
                zijn. Waar op u <strong className="text-text">dwingend consumentenrecht</strong> van toepassing is,
                prevaleert dat boven tegenstrijdige bepalingen in deze voorwaarden, voor zover dat uit dwingend recht
                volgt.
              </p>
            </div>
          </section>
          <section>
            <h2 className="mb-2 font-semibold text-text">2. Proefperiode, abonnementen en betaling</h2>
            <div className="space-y-3 text-sm text-text-muted">
              <p>
                <strong className="text-text">Proefperiode.</strong> CalqTool™ kan een gratis proefperiode
                aanbieden na registratie. De proefperiode duurt <strong className="text-text">7 dagen</strong>{' '}
                vanaf het moment dat uw account actief is, en eindigt zodra de in uw account of op de website
                vermelde <strong className="text-text">gebruikslimieten</strong> zijn bereikt —{' '}
                <strong className="text-text">wat eerder komt</strong>. Richtlijn voor limieten tijdens de
                proefperiode (indien van toepassing) kunnen onder meer gelden: maximaal{' '}
                <strong className="text-text">2</strong> tekeninganalyses, maximaal{' '}
                <strong className="text-text">1</strong> gelijktijdig actief dossier en maximaal{' '}
                <strong className="text-text">3</strong> exports (PDF en/of spreadsheets) in totaal — tenzij in de dienst
                of per e-mail andere aantallen worden gecommuniceerd.
              </p>
              <p>
                Tijdens de proefperiode krijgt u toegang tot de functionaliteit zoals beschreven voor het
                desbetreffende pakket (vergelijkbaar met het Plus-pakket), tenzij anders aangegeven.
              </p>
              <p>
                <strong className="text-text">Na de proefperiode</strong> is voor voortgezet gebruik van de
                dienst een betaald abonnement vereist, tenzij CalqTool™ schriftelijk anders overeenkomt.
                Zonder geldig abonnement kan toegang worden beperkt of beëindigd.
              </p>
              <p>
                <strong className="text-text">Abonnementen en looptijd.</strong> Abonnementen worden aangeboden onder
                de namen <strong className="text-text">Basis</strong>, <strong className="text-text">Plus</strong>,{' '}
                <strong className="text-text">Pro</strong> en <strong className="text-text">Enterprise</strong>{' '}
                (Enterprise uitsluitend op aanvraag en per offerte). Een betaald abonnement heeft een vaste
                factureringsperiode van <strong className="text-text">één maand</strong> of{' '}
                <strong className="text-text">één jaar</strong>, afhankelijk van uw keuze bij afsluiting of wijziging.
                Per pakket kunnen limieten gelden voor onder meer het aantal gebruikers, gelijktijdige dossiers,
                tekeninganalyses per maand en exports per maand. Details staan op de{' '}
                <Link to="/prijzen" className="font-medium text-primary hover:underline">
                  pakkettenpagina
                </Link>{' '}
                en/of in uw order- of betaalbevestiging. Hoe tekeninganalyses worden geteld (bijvoorbeeld per gestarte
                analyse-run) staat nader beschreven op de pakkettenpagina, voor zover van toepassing.
              </p>
              <p>
                <strong className="text-text">Automatische verlenging.</strong> Tenzij en voor zover u het abonnement
                tijdig en volgens de in de applicatie of via de betaalpartner (Stripe) beschikbare procedure beëindigt,
                wordt een lopend abonnement aan het einde van elke factureringsperiode{' '}
                <strong className="text-text">stilzwijgend verlengd</strong> met een nieuwe periode van dezelfde duur,
                tegen het dan geldende tarief voor het gekozen pakket. U wordt geacht bij afsluiting te zijn geïnformeerd
                over deze automatische verlenging. Facturen en operationele e-mails over betalingen (waaronder
                herinneringen bij mislukte incasso of openstaande betalingsbevestiging) kunnen (ook) worden verstuurd
                door CalqTool™ en/of door de betaalpartner (Stripe), voor zover technisch ingeschakeld — zie verder
                onder “Betaling en incasso”.
              </p>
              <p>
                <strong className="text-text">Opzegging door u.</strong> U kunt uw betaalde abonnement beëindigen via de
                in de applicatie beschikbare facturatieomgeving en/of via het{' '}
                <strong className="text-text">klantportaal</strong> van de betaalpartner (Stripe), voor zover voor uw
                account beschikbaar. Tenzij in dat portaal of tijdens de opzegging uitdrukkelijk anders wordt aangegeven,
                eindigt het abonnement op het <strong className="text-text">einde van de lopende factureringsperiode</strong>{' '}
                waarin de opzegging is aangevraagd (opzegging aan het einde van de periode). U behoudt tot dat tijdstip
                toegang voor zover uw account niet om andere redenen is geschorst. Een reeds verschuldigde of betaalde
                periode wordt niet naar evenredigheid terugbetaald, behalve voor zover{' '}
                <strong className="text-text">dwingend recht</strong> (bijvoorbeeld consumentenrecht) anders vereist.
              </p>
              <p>
                <strong className="text-text">Opzegging of beëindiging door CalqTool™.</strong> CalqTool™ mag het
                abonnement en/of de toegang tot de dienst beëindigen of opschorten met inachtneming van dwingend recht,
                onder meer bij ernstige of herhaalde contractbreuk, fraude, of na ingebrekestelling en redelijke termijn
                indien gerechtvaardigd. Bij beëindiging wegens wanbetaling mag CalqTool™ de toegang opschorten na
                redelijke aankondiging voor zover toegestaan door wet of door de betaalpartnerprocedures.
              </p>
              <p>
                <strong className="text-text">Upgrade en downgrade.</strong> U kunt — voor zover u daartoe bevoegd bent
                binnen uw organisatie — het gekozen pakket of de factureringsperiode wijzigen via de facturatieomgeving in
                de applicatie en/of via het <strong className="text-text">Stripe-klantportaal</strong> (voor zover voor uw
                account beschikbaar). Wijzigingen worden technisch door Stripe verwerkt. Voor ons klantportaal is de
                bedoeling dat kosten en tegoeden <strong className="text-text">evenredig (pro rata)</strong> worden
                verrekend ten opzichte van de rest van de lopende factureringsperiode en dat aanpassingen{' '}
                <strong className="text-text">rondom het moment van de wijziging</strong> in rekening worden gebracht,
                voor zover Stripe dat toepast; exacte bedragen en tijdstippen volgen uit de door Stripe gegenereerde
                factuur en uw betaalinstellingen. Geen enkele wijziging doet af aan rechten die uit dwingend recht
                voortvloeien.
              </p>
              <p>
                <strong className="text-text">Prijzen en btw.</strong> Tenzij uitdrukkelijk anders vermeld, zijn de op de
                website vermelde prijzen voor <strong className="text-text">zakelijke afnemers</strong>{' '}
                <strong className="text-text">exclusief omzetbelasting (BTW)</strong>. Jaarabonnementen kunnen een korting
                ten opzichte van twaalf maanden maandbetaling bevatten zoals op de site vermeld.
              </p>
              <p>
                <strong className="text-text">Wijziging van tarieven (catalogusprijzen).</strong> CalqTool™ mag de
                gepubliceerde catalogusprijzen wijzigen. Voor <strong className="text-text">bestaande</strong>{' '}
                abonnementen geldt een prijswijziging — voor zover niet anders door wet of door Stripe voorgeschreven —
                uiterlijk na een redelijke aankondigingsperiode en/of vanaf de eerstvolgende factureringsperiode na
                bekendmaking, in lijn met wat in de applicatie, per e-mail of via de betaalpartner wordt gecommuniceerd.
                Voortgezet gebruik na ingang van de wijziging kan als aanvaarding worden geïnterpreteerd, voor zover
                toegestaan door dwingend recht.
              </p>
              <p>
                <strong className="text-text">Betaling en incasso (self-serve).</strong> Betaling voor standaardpakketten
                verloopt via een externe betaaldienstverlener (<strong className="text-text">Stripe</strong>). Nieuwe
                afsluiting verloopt via een door Stripe gehoste betaalpagina (Checkout). Op de betaling zijn de
                voorwaarden en het privacybeleid van Stripe van toepassing. Beheer van betaalmethoden, facturen en
                opzegging kan via het <strong className="text-text">door Stripe gehoste klantportaal</strong>. Wanneer u
                dat portaal verlaat na het beheren van uw gegevens, wordt u in de regel teruggeleid naar de pagina{' '}
                <strong className="text-text">Abonnement &amp; facturatie</strong> in de CalqTool-applicatie (pad{' '}
                <strong className="text-text">/account/billing</strong>), overeenkomstig de omleidingsinstelling bij Stripe;
                afwijkingen kunnen optreden door browserinstellingen of tussenkomst van Stripe.
              </p>
              <p>
                <strong className="text-text">Automatische incasso en e-mailherinneringen (Stripe).</strong> Terugkerende
                bedragen worden <strong className="text-text">automatisch</strong> geïncasseerd voor zover u daarvoor een
                geldige betaalmethode heeft opgegeven, tenzij u tijdig opzegt of de wet of Stripe anders voorschrijft.
                Stripe kan u — naast factuur- en betaalbevestigingsmails — e-mails sturen als een betaling mislukt of als
                een betaling <strong className="text-text">nadere bevestiging</strong> van u vereist (bijvoorbeeld sterke
                klantauthenticatie bij kaartbetalingen). Voor zover in onze Stripe-configuratie is ingesteld, kunnen{' '}
                <strong className="text-text">herinneringsmails voor niet-voltooide betalingsbevestiging</strong> worden
                verstuurd op <strong className="text-text">1, 3 en 5 dagen</strong> na het ontstaan van die openstaande
                bevestiging, tot de bevestiging is voltooid of de betaling verloopt volgens de regels van Stripe en uw
                bank of kaartuitgever. Tijdstippen kunnen in uitzonderlijke gevallen afwijken (bijvoorbeeld door
                verwerking, tijdzones of onderbrekingen). Herinneringen en retries bij mislukte automatische incasso
                volgen de instellingen voor <strong className="text-text">omzetherstel</strong> (revenue recovery) in
                Stripe, voor zover ingeschakeld. Zie ook het{' '}
                <Link to="/privacy" className="font-medium text-primary hover:underline">
                  privacybeleid
                </Link>
                .
              </p>
              <p>
                <strong className="text-text">Terugkerende factuur per e-mail (send invoice).</strong> Indien u ooit een
                abonnement of factuur heeft waarbij Stripe facturen uitsluitend per e-mail ter betaling aanbiedt (
                <strong className="text-text">send invoice</strong>), kunnen aanvullende herinneringen voor openstaande
                facturen via Stripe lopen conform de daar ingestelde schema’s. Voor de standaard CalqTool-pakketten
                geldt doorgaans <strong className="text-text">automatische incasso</strong> zoals hierboven beschreven.
              </p>
              <p>
                Voor Enterprise en maatwerk kunnen afwijkende betalingsvoorwaarden in een offerte of schriftelijke
                overeenkomst gelden.
              </p>
              <p>
                <strong className="text-text">Niet-betaling en storneringen.</strong> Bij uitblijven van tijdige betaling
                of bij terugboeking/stornering mag CalqTool™ — na redelijke ingebrekestelling waar wettelijk nodig — de
                toegang tot de dienst beperken of opschorten totdat het openstaande bedrag is voldaan of de geschilregeling
                is afgerond, zonder afbreuk te doen aan overige rechten of remedies. Eventuele incassokosten kunnen aan u
                worden doorbelast voor zover wettelijk toegestaan.
              </p>
              <p>
                <strong className="text-text">Consumenten (natuurlijke personen buiten beroep of bedrijf).</strong> Als u
                handelt als <strong className="text-text">consument</strong> in de zin van het Nederlandse en Europese
                consumentenrecht, worden de rechten die u op grond van dwingende wetgeving toekomen — waaronder waar
                van toepassing het <strong className="text-text">herroepingsrecht</strong> bij overeenkomsten op afstand
                (waaronder artikel 6:230m e.v. van het Burgerlijk Wetboek) — niet uitgesloten of beperkt door deze
                voorwaarden, behalve voor zover de wet uitdrukkelijk afwijking toestaat. Voor digitale diensten die met
                uw <strong className="text-text">uitdrukkelijke voorafgaande instemming</strong> vóór het verstrijken van
                de herroepingstermijn worden uitgevoerd, kan het herroepingsrecht vervallen indien u heeft erkend daarmee
                uw herroepingsrecht te verliezen, zoals de wet voorschrijft. Onverminderd het voorgaande blijft de
                toepasselijkheid van specifieke consumentenbeschermingsregels (waaronder transparantie rond
                abonnementsafsluiting en automatische verlenging) onverkort van kracht waar de wet dat vereist.
              </p>
              <p>
                <strong className="text-text">Indicatieve aard van uitvoer.</strong> De berekeningen en documenten uit
                CalqTool™ zijn <strong className="text-text">indicatief</strong> en niet bindend tenzij partijen dat
                contractueel anders overeenkomen.
              </p>
            </div>
          </section>
          <section>
            <h2 className="mb-2 font-semibold text-text">3. Gebruik en fair use</h2>
            <div className="space-y-3 text-sm text-text-muted">
              <p>
                U gebruikt de dienst uitsluitend voor rechtmatige doeleinden. U deelt geen inloggegevens en
                houdt uw account veilig.
              </p>
              <p>
                Het is niet toegestaan de prijsbibliotheek, catalogus of andere inhoud van CalqTool™ op
                oneigenlijke wijze te verzamelen, te kopiëren of te reconstrueren (bijvoorbeeld door geautomatiseerd
                of systematisch opvragen buiten het normale gebruik van de applicatie). CalqTool™ kan technische
                maatregelen en limieten (rate limits) hanteren om misbruik te voorkomen.
              </p>
              <p>
                De <strong className="text-text">documentenbibliotheek</strong> (bestanden per dossier, waaronder
                tekeningen en PDF’s) is bedoeld voor normaal projectgebruik, waaronder opslag en hergebruik voor
                tekeninganalyse zoals in de applicatie aangeboden. Oneigenlijk of excessief gebruik kan worden
                beperkt met dezelfde technische maatregelen als hierboven.
              </p>
              <p>
                <strong className="text-text">Sessies en automatische afmelding.</strong> CalqTool kan technische
                maatregelen hanteren om misbruik te beperken, waaronder het beëindigen van uw actieve inlogsessie in de
                webapp na een periode zonder gebruikersactiviteit (beveiliging, met name op gedeelde apparaten). Dit is
                naast — en los van — de maximale technische geldigheid van sessiegegevens op de server. Nadere uitleg
                staat in het{' '}
                <Link to="/privacy" className="font-medium text-primary hover:underline">
                  privacybeleid
                </Link>{' '}
                en{' '}
                <Link to="/cookies" className="font-medium text-primary hover:underline">
                  cookiebeleid
                </Link>
                .
              </p>
              <p>
                Voor cookies en statistiek verwijzen wij naar het{' '}
                <Link to="/cookies" className="font-medium text-primary hover:underline">
                  cookiebeleid
                </Link>{' '}
                en{' '}
                <Link to="/privacy" className="font-medium text-primary hover:underline">
                  privacybeleid
                </Link>
                .
              </p>
            </div>
          </section>
          <section>
            <h2 className="mb-2 font-semibold text-text">4. Aansprakelijkheid</h2>
            <p className="text-sm text-text-muted">
              CalqTool™ is niet aansprakelijk voor beslissingen genomen op basis van de berekeningen. Voor
              definitieve offertes en vergunningen is altijd een vakman nodig.
            </p>
          </section>
          <section>
            <h2 className="mb-2 font-semibold text-text">5. Wijzigingen</h2>
            <p className="text-sm text-text-muted">
              CalqTool kan deze voorwaarden en de dienst aanpassen. Wijzigingen worden gepubliceerd op de website met een
              bijgewerkte datum &quot;Laatst bijgewerkt&quot;. Voortgezet gebruik na publicatie kan als aanvaarding
              worden beschouwd tenzij wij anders vermelden of wettige hogere eisen gelden. Controleer de voorwaarden
              periodiek, met name bij verlenging van een abonnement.
            </p>
          </section>
          <section>
            <h2 className="mb-2 font-semibold text-text">6. Toepasselijk recht en geschillen</h2>
            <p className="text-sm text-text-muted">
              Op deze voorwaarden en het gebruik van CalqTool is uitsluitend <strong className="text-text">Nederlands recht</strong>{' '}
              van toepassing. Geschillen worden bij uitsluiting voorgelegd aan de bevoegde rechter in Nederland, tenzij
              dwingend consumentenrecht anders voorschrijft.
            </p>
          </section>
          <p className="text-xs text-text-muted">
            Deze tekst is bedoeld als contractueel kader voor het gebruik van CalqTool™. Voor definitieve juridische
            toetsing, met name bij een mix van zakelijke en particuliere klanten of bij grensoverschrijdende levering,
            raden wij aan een advocaat of juridisch adviseur te raadplegen.
          </p>
          <Link
            to="/"
            className="inline-flex text-primary transition-colors duration-200 hover:underline"
          >
            ← Terug naar home
          </Link>
        </div>
      </AnimatedSection>
    </div>
  );
}
